import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { G } from './glyphs';

// THE CUSTOMER'S SUCCESS TOAST (Freddi, 2026-10-11: "a success toast of sorts when a
// user uploads proof of deposit successfully, along with uploading vaccination …
// in line with our design files, themes etc").
//
// The app's own grammar for good news, softly (2026-10-11): a light card that fades up
// from the foot of the screen above the tab bar, a pale green tick disc, one bold line saying what
// happened and one quiet line saying what happens next. Announced politely to a
// screen reader; gone after a few seconds, or at once on a tap. One at a time — a
// second replaces the first. Without a provider, `show` does nothing, so a page
// rendered on its own (the unit renders) is unaffected.

export interface CustomerToastMessage {
  title: string;
  detail?: string;
  /** Wait this long before rising — e.g. for a drawer to finish sliding away, so the toast is seen (2026-10-11). */
  afterMs?: number;
}
interface Shown extends CustomerToastMessage { id: number }

const Ctx = createContext<((t: CustomerToastMessage) => void) | null>(null);
const NOOP = () => {};

export function useCustomerToast(): (t: CustomerToastMessage) => void {
  return useContext(Ctx) ?? NOOP;
}

export const TOAST_MS = 4200;

const LEAVE_MS = 360;

export function CustomerToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Shown | null>(null);
  // Leaving is a state of its own, so the pill fades back down before it unmounts.
  const [leaving, setLeaving] = useState(false);
  const seq = useRef(0);
  const show = useCallback((t: CustomerToastMessage) => {
    const go = () => { seq.current += 1; setLeaving(false); setToast({ ...t, id: seq.current }); };
    if (t.afterMs) window.setTimeout(go, t.afterMs); else go();
  }, []);
  const dismiss = useCallback(() => setLeaving(true), []);
  useEffect(() => {
    if (!toast || leaving) return;
    const timer = window.setTimeout(() => setLeaving(true), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast, leaving]);
  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => { setToast(null); setLeaving(false); }, LEAVE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);
  return (
    <Ctx.Provider value={show}>
      {children}
      {typeof document !== 'undefined' && createPortal(<CustomerToastView toast={toast} leaving={leaving} onDismiss={dismiss} />, document.body)}
    </Ctx.Provider>
  );
}

export function CustomerToastView({ toast, onDismiss, leaving = false }: { toast: Shown | null; onDismiss: () => void; leaving?: boolean }) {
  if (!toast) return null;
  return (
    <div className={'cp-toast' + (leaving ? ' is-leaving' : '')} role="status" aria-live="polite">
      <button type="button" key={toast.id} className="cp-toast-pill" onClick={onDismiss} aria-label={`${toast.title}. Dismiss`}>
        <span className="cp-toast-ic" aria-hidden="true">{G.tick({ size: 14 })}</span>
        <span className="cp-toast-txt"><b>{toast.title}</b>{toast.detail && <small>{toast.detail}</small>}</span>
      </button>
    </div>
  );
}
